import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Check, ShieldCheck, ExternalLink } from 'lucide-react';
import { api, throwIfNotOk } from '../../lib/api';
import { qk } from '../../lib/queryKeys';
import { Card, CardLabel } from '../asset-v2/shared';
import { Button } from '@siliconx/shared/ui/Button';
import { SafeLink } from '@siliconx/shared/ui/SafeLink';
import { InsightBanner } from '@siliconx/shared/ui/InsightBanner';

/**
 * SIL-585 (Wave 4/UI, spec §8 seller flow) — "Verify this asset" action.
 *
 * Coarse states only (awaiting -> running -> results_ready), poll/refresh —
 * NO live-streaming surface (the serverless decision this spec avoided
 * buying, §3). On mint, presents the one-time, self-verifying install
 * command (§8 finding 4: checksum-gated before it ever runs `bash`).
 *
 * A pre-merge review (SIL-584/SIL-585) removed a "no shell access? run it
 * from a browser" fallback that used to be offered alongside this command —
 * it opened `harness.html?token=...` directly, but the harness only reads
 * relay-injected `window.__SILICONX_RUN_CONFIG__` (apps/verify-harness/src/
 * harness.ts) and never redeems a token or POSTs results when loaded
 * directly, so that path recorded nothing while looking like a working
 * option.
 *
 * SIL-605 restores a real no-shell path, but a different one: the
 * asset-specific verification link minted via SIL-599's
 * `POST /api/client/verification-links/assets` (reused here, not a new
 * endpoint). Unlike the abandoned `harness.html?token=` link above, this
 * one is a first-class, publicly-resolvable URL (`/v/:code`, SIL-598/600)
 * that a browser can open directly and which drives its own run — it is
 * now the PRIMARY path (no download, no shell), with the checksum-gated
 * one-liner kept as the secondary option for headless/remote-terminal
 * cases. Do not conflate this with the org-wide link on the
 * `/verification-links` settings page: that one auto-matches by GPU
 * fingerprint; this one resolves straight to this asset, no matching.
 * Bulk-create is idempotent for an asset that already has an active link,
 * so minting on every click is safe — it never produces a second link.
 */

type RunStatus = 'awaiting' | 'running' | 'results_ready' | 'expired';

interface LatestRun {
  id: string;
  assetId: string;
  tier: 1 | 2 | 3;
  status: RunStatus;
  verdict: string | null;
  startedAt: string | null;
  completedAt: string | null;
}

interface MintedRun {
  runId: string;
  fetchTokenExpiresAt: string;
  installCommand: string;
}

interface AssetLink {
  assetId: string;
  shortCode: string;
  fullUrl: string;
}

const STATUS_LABEL: Record<RunStatus, string> = {
  awaiting: 'Awaiting run',
  running: 'Running',
  results_ready: 'Complete',
  expired: 'Expired',
};

const POLLING_STATUSES: RunStatus[] = ['awaiting', 'running'];

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        void navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
      className="inline-flex shrink-0 items-center gap-1 rounded-md border border-border px-2 py-1 text-xs font-medium text-muted-foreground hover:bg-muted"
    >
      {copied ? <Check className="size-3" /> : <Copy className="size-3" />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

function CommandRow({ label, command }: { label: string; command: string }) {
  return (
    <div className="space-y-1">
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      <div className="flex items-start gap-2 rounded-md border border-border bg-muted px-2 py-1.5">
        <code className="min-w-0 flex-1 overflow-x-auto whitespace-pre text-xs text-foreground">
          {command}
        </code>
        <CopyButton text={command} />
      </div>
    </div>
  );
}

function RunProgress({ runId, onSettled }: { runId: string; onSettled: () => void }) {
  const { data: run } = useQuery<LatestRun>({
    queryKey: qk.verificationRun(runId),
    queryFn: async () => {
      const res = await api.api.client.verification.runs[':runId'].$get({ param: { runId } });
      await throwIfNotOk(res);
      return (await res.json()) as unknown as LatestRun;
    },
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status && POLLING_STATUSES.includes(status) ? 4000 : false;
    },
  });

  useEffect(() => {
    if (run && !POLLING_STATUSES.includes(run.status)) onSettled();
  }, [run, onSettled]);

  if (!run) return null;

  return (
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      <span
        className={
          run.status === 'results_ready'
            ? 'inline-block size-2 rounded-full bg-emerald-500'
            : run.status === 'expired'
              ? 'inline-block size-2 rounded-full bg-red-500'
              : 'inline-block size-2 animate-pulse rounded-full bg-amber-500'
        }
        aria-hidden
      />
      {STATUS_LABEL[run.status]}
      {run.status === 'results_ready' && (
        <span className="text-foreground">— results synthesized below.</span>
      )}
    </div>
  );
}

export function VerifyAssetAction({ assetId }: { assetId: string }) {
  const queryClient = useQueryClient();

  // On mount, recover an in-progress run across a reload (coarse-states
  // refresh model, §8 — no in-memory-only state to lose).
  const { data: latestRun } = useQuery<LatestRun | null>({
    queryKey: ['client-asset-verification-latest-run', assetId],
    queryFn: async () => {
      const res = await api.api.client.verification.assets[':assetId']['latest-run'].$get({
        param: { assetId },
      });
      if (res.status === 404) return null;
      await throwIfNotOk(res);
      return (await res.json()) as unknown as LatestRun;
    },
  });

  const [minted, setMinted] = useState<MintedRun | null>(null);
  const [activeRunId, setActiveRunId] = useState<string | null>(null);

  useEffect(() => {
    if (latestRun && POLLING_STATUSES.includes(latestRun.status)) {
      setActiveRunId(latestRun.id);
    }
  }, [latestRun]);

  const mint = useMutation<MintedRun, Error, void>({
    mutationFn: async () => {
      const res = await api.api.client.verification.runs.$post({ json: { assetId } });
      await throwIfNotOk(res);
      return (await res.json()) as unknown as MintedRun;
    },
    onSuccess: (data) => {
      setMinted(data);
      setActiveRunId(data.runId);
    },
  });

  // Asset-specific browser link (SIL-605), reusing SIL-599's bulk-create —
  // idempotent for an asset with an active link, so minting on every click
  // never produces a second link; the existing one is returned instead.
  const [assetLink, setAssetLink] = useState<AssetLink | null>(null);
  const mintLink = useMutation<AssetLink, Error, void>({
    mutationFn: async () => {
      const res = await api.api.client['verification-links'].assets.$post({ json: { assetIds: [assetId] } });
      await throwIfNotOk(res);
      const links = (await res.json()) as unknown as AssetLink[];
      const link = links[0];
      if (!link) throw new Error('verification-links/assets returned no link');
      return link;
    },
    onSuccess: (data) => setAssetLink(data),
  });

  const handleVerifyClick = () => {
    mint.mutate();
    mintLink.mutate();
  };

  const handleSettled = () => {
    setActiveRunId(null);
    setMinted(null);
    void queryClient.invalidateQueries({ queryKey: qk.assets.verificationState(assetId) });
    void queryClient.invalidateQueries({ queryKey: qk.assets.passportAll() });
    void queryClient.invalidateQueries({ queryKey: ['client-asset-verification-latest-run', assetId] });
  };

  // SIL-1230 — cancel the poll and reset the flow to pre-verification state
  // so the user isn't stuck watching a poll they can't get out of (e.g. no
  // WebGPU-capable browser available to actually finish the run). Client-
  // side only: stops polling, drops the minted link/command, and brings back
  // the "Verify this asset" button. Does not attempt to cancel the run
  // server-side.
  const handleCancel = () => {
    if (activeRunId) {
      void queryClient.cancelQueries({ queryKey: qk.verificationRun(activeRunId) });
    }
    setActiveRunId(null);
    setMinted(null);
    setAssetLink(null);
  };

  return (
    <Card className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <ShieldCheck className="size-4 text-muted-foreground" />
          <CardLabel>Active verification</CardLabel>
        </div>
        {!activeRunId && (
          <Button size="sm" onClick={handleVerifyClick} disabled={mint.isPending}>
            {mint.isPending ? 'Starting…' : 'Verify this asset'}
          </Button>
        )}
        {activeRunId && (
          <Button size="sm" variant="secondary" onClick={handleCancel}>
            Cancel
          </Button>
        )}
      </div>

      <p className="text-xs text-muted-foreground">
        Runs a stress test against this hardware and posts the result to its passport and any
        active listing. Takes a few minutes; you don&apos;t need to keep this page open.
      </p>

      <InsightBanner tone="neutral" lead="Before you run this">
        <ul className="list-disc space-y-1 pl-4">
          <li>
            Needs Chrome or another Chromium-based browser with WebGPU support — WebGPU support
            varies by browser, so this isn&apos;t guaranteed on every browser.
          </li>
          <li>
            Needs a real GPU the browser can reach — not a VM&apos;s software-rendered display, and
            not a headless or remote session without GPU passthrough.
          </li>
          <li>
            Runs for several minutes under full GPU load. If the asset has more than one
            accelerator, each is tested in its own sequential pass, so total time scales with
            accelerator count.
          </li>
          <li>Keep the tab open and the machine awake until it finishes.</li>
        </ul>
      </InsightBanner>

      {mint.isError && (
        <p className="text-xs text-red-600">Couldn&apos;t start verification. {mint.error.message}</p>
      )}

      {minted && (
        <div className="space-y-3 rounded-md border border-border p-3">
          {assetLink && (
            <div className="space-y-1.5">
              <p className="text-xs text-muted-foreground">
                Open this link in a browser on the machine hosting this asset — no download or
                shell access needed. It resolves straight to this asset (unlike the org-wide link
                on the Verification Links settings page, which auto-matches by GPU fingerprint).
              </p>
              <div className="flex items-start gap-2 rounded-md border border-border bg-muted px-2 py-1.5">
                <SafeLink
                  href={assetLink.fullUrl}
                  className="inline-flex min-w-0 flex-1 items-center gap-1 overflow-x-auto whitespace-pre text-xs font-medium text-foreground underline decoration-dotted"
                >
                  <ExternalLink className="size-3 shrink-0" />
                  {assetLink.fullUrl}
                </SafeLink>
                <CopyButton text={assetLink.fullUrl} />
              </div>
            </div>
          )}

          <details className="text-xs text-muted-foreground">
            <summary className="cursor-pointer font-medium text-muted-foreground">
              No browser access? Run this instead
            </summary>
            <div className="mt-2 space-y-2">
              <p>
                Run this on the machine hosting this asset (requires shell access). It downloads
                the verification runner, checks its checksum, and only then runs it — nothing
                executes unverified. The link expires{' '}
                {new Date(minted.fetchTokenExpiresAt).toLocaleString()}.
              </p>
              <p>
                <strong className="font-medium text-foreground">Run this as a regular user.</strong>{' '}
                Do not use sudo.
              </p>
              <CommandRow label="Run now (Linux)" command={minted.installCommand} />
              <p>
                <strong className="font-medium text-foreground">Windows or macOS host?</strong> Use the
                browser link above instead. The verification runner is Linux-only — it reads{' '}
                <code>/sys</code> and <code>/dev/dri</code> and calls <code>lspci</code>, none of which
                have a Windows equivalent — and there is no PowerShell runner. (The separate hardware
                <em>identification</em> probe on the Customs page does have a Windows version; it reports
                identity, not measured performance.)
              </p>
            </div>
          </details>
        </div>
      )}

      {activeRunId && <RunProgress runId={activeRunId} onSettled={handleSettled} />}
    </Card>
  );
}
